/**
 * Header Image Storage Utilities
 * 
 * Provides session storage management for article header images.
 * Requirements: 3.1, 3.2
 */

/**
 * Supported aspect ratios for header images
 */
export type AspectRatio = 
  | '1:1' 
  | '2:3' 
  | '3:2' 
  | '3:4' 
  | '4:3' 
  | '4:5' 
  | '5:4' 
  | '9:16' 
  | '16:9' 
  | '21:9';

/**
 * Data structure for storing header image information
 */
export interface HeaderImageData {
  imageUrl: string;        // Base64 data URL
  aspectRatio: AspectRatio;
  prompt: string;
  style: string;
  generatedAt: number;     // Unix timestamp
}

/**
 * CSS class mapping for aspect ratios (Tailwind classes)
 */
export const ASPECT_RATIO_CSS: Record<AspectRatio, string> = {
  '1:1': 'aspect-square',
  '2:3': 'aspect-[2/3]',
  '3:2': 'aspect-[3/2]',
  '3:4': 'aspect-[3/4]',
  '4:3': 'aspect-[4/3]',
  '4:5': 'aspect-[4/5]',
  '5:4': 'aspect-[5/4]',
  '9:16': 'aspect-[9/16]',
  '16:9': 'aspect-video',
  '21:9': 'aspect-[21/9]',
};

/**
 * Storage key prefix for article header images
 */
const STORAGE_KEY_PREFIX = 'article-header-image-';

/**
 * Generates a unique storage key for an article's header image
 * @param articleId - The unique identifier of the article
 * @returns The session storage key for the article's header image
 */
export function getStorageKey(articleId: string): string {
  return `${STORAGE_KEY_PREFIX}${articleId}`;
}

/**
 * Image Generation Mode
 * - 'easy': One-click generation with automatic prompt (default)
 * - 'advanced': Multi-step wizard with prompt review/edit
 */
export type ImageGenerationMode = 'easy' | 'advanced';

/**
 * LocalStorage key for persisting image generation mode preference
 */
export const IMAGE_GENERATION_MODE_KEY = 'vantage-draft-image-generation-mode';

/**
 * Gets the user's preferred image generation mode from localStorage
 * @returns The stored mode or 'easy' as default
 */
export function getImageGenerationMode(): ImageGenerationMode {
  try {
    const stored = localStorage.getItem(IMAGE_GENERATION_MODE_KEY);
    return stored === 'advanced' ? 'advanced' : 'easy';
  } catch {
    return 'easy';
  }
}

/**
 * Saves the user's preferred image generation mode to localStorage
 * @param mode - The mode to save
 */
export function setImageGenerationMode(mode: ImageGenerationMode): void {
  try {
    localStorage.setItem(IMAGE_GENERATION_MODE_KEY, mode);
  } catch {
    // Ignore storage errors (e.g., private browsing mode)
  }
}
